import { ReactNode, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";

export type SelectionMode = "none" | "single" | "multi";
export type SortDir = "asc" | "desc";

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
  headerClassName?: string;
  sortValue?: (row: T) => string | number | Date | null | undefined;
}

interface DataTableProps<T> {
  columns: Array<DataTableColumn<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selection?: SelectionMode;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  defaultSort?: { key: string; dir: SortDir };
}

const compareValues = (a: unknown, b: unknown): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const alignClass = (align: DataTableColumn<unknown>["align"]) =>
  align === "right"
    ? "text-right"
    : align === "center"
      ? "text-center"
      : "text-left";

export const DataTable = <T,>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selection = "none",
  selectedIds = [],
  onSelectionChange,
  defaultSort,
}: DataTableProps<T>) => {
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(
    defaultSort ?? null,
  );

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const getter = col.sortValue;
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = compareValues(getter(a), getter(b));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort, columns]);

  const toggleSort = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allIds = useMemo(() => sortedRows.map(rowKey), [sortedRows, rowKey]);
  const allSelected =
    selection === "multi" &&
    allIds.length > 0 &&
    allIds.every((id) => selectedSet.has(id));
  const someSelected =
    selection === "multi" &&
    !allSelected &&
    allIds.some((id) => selectedSet.has(id));

  const toggleOne = (id: string) => {
    if (!onSelectionChange) return;
    if (selection === "single") {
      onSelectionChange(selectedSet.has(id) ? [] : [id]);
      return;
    }
    if (selection === "multi") {
      const next = new Set(selectedSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onSelectionChange(Array.from(next));
    }
  };

  const toggleAll = () => {
    if (!onSelectionChange || selection !== "multi") return;
    onSelectionChange(allSelected ? [] : allIds);
  };

  const showSelection = selection !== "none";

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/50 text-muted-foreground text-left">
            {showSelection && (
              <th className="px-4 py-3 font-medium w-10">
                {selection === "multi" && (
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-border"
                  />
                )}
              </th>
            )}
            {columns.map((col) => {
              const isSortable = !!col.sortValue;
              const isActive = sort?.key === col.key;
              const justify =
                col.align === "right"
                  ? "justify-end"
                  : col.align === "center"
                    ? "justify-center"
                    : "justify-start";
              return (
                <th
                  key={col.key}
                  className={`px-4 py-3 font-medium ${alignClass(col.align)} ${col.headerClassName ?? ""}`}
                  aria-sort={
                    isActive
                      ? sort!.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                >
                  {isSortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key)}
                      className={`inline-flex items-center gap-1 ${justify} w-full select-none hover:text-foreground ${
                        isActive ? "text-foreground" : ""
                      }`}
                    >
                      <span>{col.header}</span>
                      {isActive ? (
                        sort!.dir === "asc" ? (
                          <ChevronUp className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )
                      ) : (
                        <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            const id = rowKey(row);
            const isSelected = selectedSet.has(id);
            return (
              <tr
                key={id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-b border-border last:border-0 transition-colors ${
                  isSelected ? "bg-primary/5" : "hover:bg-muted/30"
                } ${onRowClick ? "cursor-pointer" : ""}`}
              >
                {showSelection && (
                  <td
                    className="px-4 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type={selection === "single" ? "radio" : "checkbox"}
                      aria-label={`Select row ${id}`}
                      checked={isSelected}
                      onChange={() => toggleOne(id)}
                      className="h-4 w-4 rounded border-border"
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 ${alignClass(col.align)} ${col.className ?? ""}`}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
